// SearchScreen/SearchCategoryScreen (build58), with browser history and actor isolation.
const numericId = (value) => {
  const number = typeof value === 'number' || /^\d+$/.test(String(value)) ? Number(value) : NaN;
  return Number.isFinite(number) && number > 0 ? number : undefined;
};
export function searchResultTarget(kind, item) {
  const key = `${kind}Id`;
  const value = numericId(kind === 'playlist' ? item.id : (item[key] ?? item.id));
  return value == null ? null : { [key]: value };
}
export const ordinarySearchClick = (event) =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
const createId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}-${Math.random().toString(36).slice(2, 8)}`;
const describe = (location) => {
  const category = location.pathname.match(
    /^\/search\/(summary|reviews|artists|lists|mixes)\/?$/,
  )?.[1];
  return {
    key: location.key,
    category,
    query: new URLSearchParams(location.search).get('q')?.trim() || '',
  };
};
export function createSearchSession({ actor, isCurrent, send, makeId = createId }) {
  const entries = new Map();
  const requests = new Set();
  let route = null;
  let active = null;
  let pending = null;
  let disposed = false;
  const usable = () => !disposed && isCurrent();
  const record = (event) => {
    if (!actor || actor === 'guest' || !usable()) return;
    const controller = new AbortController();
    requests.add(controller);
    // Resolve rejected analytics independently; content navigation must always proceed.
    try {
      Promise.resolve(send([event], controller.signal))
        .catch(() => {})
        .finally(() => requests.delete(controller));
    } catch {
      requests.delete(controller);
    }
  };
  const remember = () => {
    if (route?.category && active?.query === route.query) entries.set(route.key, active);
  };
  const begin = (value, context, attach = true) => {
    const query = value?.trim();
    if (!query || !usable()) return null;
    active = { id: makeId(), query };
    record({ eventType: 'SEARCH_SUBMIT', searchSessionId: active.id, searchQuery: query, context });
    if (attach) remember();
    return active;
  };
  const ensure = (query, context) =>
    active?.query === query.trim() ? active : begin(query, context);
  return {
    observe(location) {
      const next = describe(location);
      if (
        route?.key === next.key &&
        route?.query === next.query &&
        route?.category === next.category
      )
        return;
      if (next.category) {
        if (pending?.query === next.query) {
          active = pending;
          pending = null;
        } else if (entries.has(next.key)) {
          active = entries.get(next.key);
        } else if (route?.category && route.query === next.query) {
          active =
            next.category !== 'summary' && route.category === 'summary'
              ? ensure(next.query, 'search_category')
              : active;
        } else active = null;
        route = next;
        remember();
      } else {
        route = next;
        // Keep the current session behind a detail, but never attach it to an unrelated URL.
      }
    },
    submit(value, context = 'search_submit') {
      pending = begin(value, context, false);
    },
    clear() {
      active = null;
      pending = null;
      if (route) entries.delete(route.key);
    },
    open({ query, kind, item, position, category }) {
      if (!usable() || !query?.trim()) return;
      // Native category opened without a session only navigates. Summary/assist may start one.
      const session = category ? active : ensure(query, 'search_assist');
      const target = searchResultTarget(kind, item);
      if (!session || session.query !== query.trim() || !target) return;
      record({
        eventType: 'SEARCH_RESULT_OPEN',
        ...target,
        searchSessionId: session.id,
        searchQuery: session.query,
        resultPosition: position,
        context: category ? `search_category_${category}` : `search_${kind}_result`,
      });
    },
    abortPending() {
      requests.forEach((request) => request.abort());
      requests.clear();
    },
    dispose() {
      disposed = true;
      requests.forEach((request) => request.abort());
      requests.clear();
      entries.clear();
      active = null;
      pending = null;
    },
  };
}
